'use client'

import { useEffect, useState } from 'react'
import { safeReload, isOnCall } from '@/app/_module/utils/safeReload'

// Stale-chunk after a deploy: an old-build browser navigates to a page whose JS
// chunk no longer exists on the new build → Next throws a ChunkLoadError and it
// bubbles here. Instead of a dead "Something went wrong" wall, auto-reload ONCE
// to pull the fresh build (new HTML + matching chunks). A sessionStorage guard
// prevents an infinite reload loop if the error is NOT actually a stale chunk.
const CHUNK_RE =
  /ChunkLoadError|Loading chunk\s+[\w-]+\s+failed|Failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i

const RELOAD_KEY = '__ic_chunk_reload_at'

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [recovering, setRecovering] = useState(false)

  useEffect(() => {
    const msg = `${error?.name || ''} ${error?.message || ''}`
    if (!CHUNK_RE.test(msg)) return
    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || '0')
      // Only auto-reload if we haven't just done so (avoids loops on a genuinely
      // broken chunk). A real stale chunk is fixed by the first reload.
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
        // safeReload waits out a live call rather than dropping it. Previously this
        // bailed out entirely during a call, which protected the call but left the
        // user parked on the error screen even after they hung up. Now it recovers
        // itself the moment the line is free.
        if (!isOnCall()) setRecovering(true)
        safeReload('stale-chunk')
      }
    } catch {
      safeReload('stale-chunk-fallback')
    }
  }, [error])

  if (recovering) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', color: '#6b7280' }}>
        Updating to the latest version…
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', fontFamily: 'system-ui, sans-serif' }}>
      <h2>Something went wrong!</h2>
      <button onClick={() => reset()} style={{ padding: '0.5rem 1.5rem', borderRadius: '0.5rem', border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer' }}>
        Try again
      </button>
    </div>
  )
}
